import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ExternalLink, FileText, Download, Loader2, Plus, Copy, Check } from 'lucide-react';
import { ProsecutionDocuments } from './ProsecutionDocuments';
import { Assignments } from './Assignments';
import { patentApi, documentApi } from '@/services/api';
import { useNotificationStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { addLoadingDocument, removeLoadingDocument } from '@/utils/loadingDocuments';

interface USApplicationDisplayProps {
  applicationData: {
    application_number: string;
    patent_info: {
      applicationMetaData: any;
    };
    pgpubDocumentMetaData?: any;
    grantDocumentMetaData?: any;
  };
  onDocumentClick?: (doc: any) => void;
  onProsecutionDocClick?: (doc: any) => void;
}

interface ContinuityInfo {
  foreign_priority: any[];
  child_applications: any[];
  parent_applications: any[];
}

// Helper component for copyable field values
function CopyableValue({ value, className = '' }: { value: string; className?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <span className="text-sm text-gray-900">{value}</span>
      <button
        onClick={handleCopy}
        className="p-1 hover:bg-gray-100 rounded transition-colors"
        title="Copy to clipboard"
      >
        {copied ? (
          <Check className="w-3 h-3 text-green-600" />
        ) : (
          <Copy className="w-3 h-3 text-gray-400 hover:text-gray-600" />
        )}
      </button>
    </div>
  );
}

export function USApplicationDisplay({ applicationData, onDocumentClick, onProsecutionDocClick }: USApplicationDisplayProps) {
  const metaData = applicationData.patent_info?.applicationMetaData;
  const [continuityInfo, setContinuityInfo] = useState<ContinuityInfo | null>(null);
  const [loadingContinuity, setLoadingContinuity] = useState(false);
  const { addNotification } = useNotificationStore();
  const { startPolling } = useJobPollingStore();

  // Fetch continuity information from prosecution docs
  useEffect(() => {
    const fetchContinuityInfo = async () => {
      if (!applicationData.application_number) return;

      setLoadingContinuity(true);
      try {
        const result = await patentApi.fetchProsecutionDocInfo(applicationData.application_number);
        const continuity: ContinuityInfo = {
          foreign_priority: result?.priority_info?.foreignPriorityBag || [],
          child_applications: result?.priority_info?.childContinuityBag || [],
          parent_applications: result?.priority_info?.parentContinuityBag || [],
        };
        setContinuityInfo(continuity);
      } catch (error) {
        console.error('Failed to fetch continuity info:', error);
        setContinuityInfo({ foreign_priority: [], child_applications: [], parent_applications: [] });
      } finally {
        setLoadingContinuity(false);
      }
    };

    fetchContinuityInfo();
  }, [applicationData.application_number]);

  // Listen for job completion events
  useEffect(() => {
    const handleJobComplete = (event: CustomEvent) => {
      const { jobId, docType, docId, success, error } = event.detail;
      const resourceTypeName = docType.charAt(0).toUpperCase() + docType.slice(1);

      if (success) {
        addNotification({
          type: 'success',
          title: `${resourceTypeName} Ready`,
          message: `${docId} has been loaded and added to your session.`,
        });
        removeLoadingDocument(docId, resourceTypeName);
      } else {
        let errorMessage = `Failed to load ${docId}. Please try again.`;

        if (error === '404') {
          errorMessage = `${docId} not found. This may be an older document. Please verify the number is correct.`;
        } else if (error === 'timeout') {
          addNotification({
            type: 'warning',
            title: 'Loading Timeout',
            message: `${docId} loading timed out. It may still complete in the background.`,
            duration: 5000,
          });
          return;
        } else if (error === 'polling-error') {
          errorMessage = `Error checking status for ${docId}.`;
        }

        addNotification({
          type: 'error',
          title: `${resourceTypeName} Load Failed`,
          message: errorMessage,
          duration: 8000,
        });

        removeLoadingDocument(docId, resourceTypeName);

        // Remove from context
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        if (docType === 'patent') {
          context.current_patents = (context.current_patents || []).filter((id: string) => id !== docId);
        } else if (docType === 'publication') {
          context.current_publications = (context.current_publications || []).filter((id: string) => id !== docId);
        }

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, [addNotification]);

  const pollJobStatus = (jobId: string, docType: string, docId: string) => {
    // Use global polling store that persists across navigation
    startPolling(jobId, docType, docId);
  };

  const addToActiveDocuments = async (docNumber: string, docType: 'patent' | 'publication') => {
    try {
      // Clean the document number - remove prefixes and clean to just digits
      let cleanedId = docNumber
        .replace(/^(patent-|publication-|US)/i, '')
        .replace(/,/g, '')
        .replace(/\//g, '')
        .replace(/[A-Z]\d*$/i, ''); // Remove kind codes

      // Check if already in context
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

      const contextKey = docType === 'patent' ? 'current_patents' : 'current_publications';
      const currentDocs = Array.isArray(context[contextKey]) ? context[contextKey] : [];

      if (currentDocs.includes(cleanedId)) {
        addNotification({
          type: 'info',
          title: 'Already Added',
          message: `${cleanedId} is already in your active documents.`,
          duration: 3000,
        });
        return;
      }

      // Call the API to fetch and load the document
      const response = await documentApi.fetchAndLoadDocument(docType, cleanedId);

      if (response?.job_id) {
        // Add to session context immediately
        context[contextKey] = [...currentDocs, cleanedId];
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        const resourceTypeName = docType.charAt(0).toUpperCase() + docType.slice(1);
        addNotification({
          type: 'info',
          title: `Loading ${resourceTypeName}`,
          message: `Processing ${cleanedId}...`,
          duration: 60000,
        });

        // Add to loading documents tracking
        addLoadingDocument(cleanedId, resourceTypeName, cleanedId);

        // Start polling for job status
        pollJobStatus(response.job_id, docType, cleanedId);
      }
    } catch (error) {
      console.error('Failed to add to active documents:', error);
      addNotification({
        type: 'error',
        title: 'Failed to Add',
        message: 'Failed to add document to active documents. Please try again.',
        duration: 5000,
      });
    }
  };

  if (!applicationData || !metaData) {
    return (
      <div className="p-6 text-center text-gray-500">
        No application data available
      </div>
    );
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };
  const statusCodes = {
    150: 'Patented Case',
    161: 'Abandoned -- Failure to Respond to an Office Action',
    250: 'Patent Expired Due to NonPayment of Maintenance Fees Under 37 CFR 1.362',
    159: 'Provisional Application Expired',
    30: 'Docketed New Case - Ready for Examination',
    218: 'RO PROCESSING COMPLETED - PLACED IN STORAGE',
    566: 'PCT - International Search Report Mailed to IB',
    41: 'Non Final Action Mailed',
    166: 'Abandoned -- File-Wrapper-Continuation Parent Application',
    19: 'Application Undergoing Preexam Processing',
    'N/A': 'Other Codes Missing',
  };

  

  const getStatusBadgeVariant = (grantDate: string | null | undefined) => {
    if (grantDate) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const getStatusText = () => {
    if (metaData?.grantDate || metaData?.patentNumber) {
      return 'Granted';
    }
    return 'Pending';
  };


  const getStatusStyling = (statusCode: string | number | null | undefined) => {
    if (!statusCode) return 'bg-gray-100 text-gray-800 border-gray-200';

    const code = parseInt(String(statusCode));

    // Patented/Granted - Green
    if (code === 150) {
      return 'bg-green-100 text-green-800 border-green-200';
    }

    // Abandoned - Red
    if (code === 161 || code === 166 || code === 159) {
      return 'bg-red-100 text-red-800 border-red-200';
    }

    // Expired - Orange/Red
    if (code === 250) {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    }

    // Active/Pending - Blue
    if (code === 30 || code === 41 || code === 19) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }

    // Processing/Administrative - Purple
    if (code === 218 || code === 566) {
      return 'bg-purple-100 text-purple-800 border-purple-200';
    }

    // Default - Gray
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const hasContinuityInfo = continuityInfo && (
    continuityInfo.foreign_priority.length > 0 ||
    continuityInfo.child_applications.length > 0 ||
    continuityInfo.parent_applications.length > 0
  );

  return (
    <Tabs defaultValue="overview" className="w-full">
      <TabsList className="mb-6">
        <TabsTrigger value="overview">Overview</TabsTrigger>
        <TabsTrigger value="continuity">Continuity</TabsTrigger>
        <TabsTrigger value="prosecution">Prosecution Documents</TabsTrigger>
        <TabsTrigger value="assignments">Assignments</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-6">
        {/* Application Overview */}
        <Card>
        <CardHeader>
          <CardTitle>Application Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
              <dt className="text-sm font-medium text-gray-500">Application Number</dt>
              <dd><CopyableValue value={applicationData.application_number} /></dd>
            </div>

            <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
              <dt className="text-sm font-medium text-gray-500">Title</dt>
              <dd className="text-right max-w-md">
                <CopyableValue value={metaData?.inventionTitle || 'N/A'} />
              </dd>
            </div>
            
            {metaData?.applicationStatusDescriptionText && (
              <div className="flex justify-between py-2">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd><CopyableValue value={metaData?.applicationStatusDescriptionText} /></dd>
              </div>
            )}

            <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
              <dt className="text-sm font-medium text-gray-500">First Inventor</dt>
              <dd><CopyableValue value={metaData?.firstInventorName || 'N/A'} /></dd>
            </div>

            <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
              <dt className="text-sm font-medium text-gray-500">Filing Date</dt>
              <dd><CopyableValue value={formatDate(metaData?.filingDate)} /></dd>
            </div>

            {metaData?.patentNumber && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                <dt className="text-sm font-medium text-gray-500">Patent Number</dt>
                <dd className="flex items-center gap-1">
                  <CopyableValue value={metaData.patentNumber} />
                  {metaData?.grantDate && (
                    <span className="text-sm text-gray-500 mr-1">(Granted {formatDate(metaData.grantDate)})</span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (onDocumentClick) {
                        onDocumentClick({
                          title: metaData.patentNumber,
                          type: 'Patent',
                          resource_id: `patent-${metaData.patentNumber}`,
                          file_type: 'patent'
                        });
                      } else {
                        addToActiveDocuments(metaData.patentNumber, 'patent');
                      }
                    }}
                    className="h-6 text-xs gap-1 px-2"
                  >
                    {onDocumentClick ? <FileText className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    {onDocumentClick ? 'View' : 'Add to Active'}
                  </Button>
                </dd>
              </div>
            )}

            {metaData?.earliestPublicationNumber && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                <dt className="text-sm font-medium text-gray-500">Publication Number</dt>
                <dd className="flex items-center gap-1">
                  <CopyableValue value={metaData.earliestPublicationNumber.replace('US', 'US ')} />
                  {metaData?.earliestPublicationDate && (
                    <span className="text-sm text-gray-500 mr-1">(Published {formatDate(metaData.earliestPublicationDate)})</span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Clean publication number: remove country code and kind code (e.g., US20230123456A1 -> 20230123456)
                      const cleanedPubNumber = metaData.earliestPublicationNumber
                        .replace(/^US/i, '')
                        .replace(/[A-Z]\d{0,2}$/i, '');
                      if (onDocumentClick) {
                        onDocumentClick({
                          title: metaData.earliestPublicationNumber,
                          type: 'Publication',
                          resource_id: `publication-${cleanedPubNumber}`,
                          file_type: 'publication'
                        });
                      } else {
                        addToActiveDocuments(cleanedPubNumber, 'publication');
                      }
                    }}
                    className="h-6 text-xs gap-1 px-2"
                  >
                    {onDocumentClick ? <FileText className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    {onDocumentClick ? 'View' : 'Add to Active'}
                  </Button>
                </dd>
              </div>
            )}

            {metaData?.examinerNameText && (
              <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                <dt className="text-sm font-medium text-gray-500">Examiner</dt>
                <dd><CopyableValue value={metaData.examinerNameText} /></dd>
              </div>
            )}

            {metaData?.docketNumber && (
              <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                <dt className="text-sm font-medium text-gray-500">Docket Number</dt>
                <dd><CopyableValue value={metaData.docketNumber} /></dd>
              </div>
            )}

            {metaData?.groupArtUnitNumber && (
              <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                <dt className="text-sm font-medium text-gray-500">Art Unit</dt>
                <dd><CopyableValue value={metaData.groupArtUnitNumber} /></dd>
              </div>
            )}

            {metaData?.applicationTypeCategory && (
              <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                <dt className="text-sm font-medium text-gray-500">Application Type</dt>
                <dd><CopyableValue value={metaData.applicationTypeCategory} /></dd>
              </div>
            )}

            {metaData?.applicationConfirmationNumber && (
              <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                <dt className="text-sm font-medium text-gray-500">Confirmation Number</dt>
                <dd><CopyableValue value={metaData.applicationConfirmationNumber} /></dd>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* External Links */}
      <Card>
        <CardHeader>
          <CardTitle>External Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {applicationData.application_number && (
              <a
                href={`https://patentcenter.uspto.gov/applications/${applicationData.application_number.replace(/[\/,]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors text-blue-600 text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                USPTO PatentCenter
              </a>
            )}

            {(metaData?.patentNumber || metaData?.earliestPublicationNumber) && (
              <a
                href={`https://assignment.uspto.gov/patent/index.html#/patent/search/resultFilter?searchInput=${(() => {
                  const numberToUse = metaData?.patentNumber || metaData?.earliestPublicationNumber;
                  return numberToUse.replace(/^US/, '').replace(/[A-Z]\d*$/, '');
                })()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors text-blue-600 text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                USPTO Assignment
              </a>
            )}

            {(metaData?.patentNumber || metaData?.earliestPublicationNumber) && (() => {
              const fullNumber = metaData?.patentNumber || metaData?.earliestPublicationNumber;
              const cleanNumber = fullNumber.replace(/^US/, '');
              const kindCodeMatch = cleanNumber.match(/([A-Z]\d*)$/);
              const isPublication = metaData?.earliestPublicationNumber && !metaData?.patentNumber;

              let kindCode;
              if (isPublication) {
                kindCode = kindCodeMatch ? kindCodeMatch[1] : 'A1';
              } else {
                if (metaData?.patentNumber && metaData?.earliestPublicationNumber) {
                  kindCode = 'B2';
                } else {
                  kindCode = 'B1';
                }
                if (kindCodeMatch) {
                  kindCode = kindCodeMatch[1];
                }
              }

              let nrValue = cleanNumber;
              if (!kindCodeMatch) {
                nrValue = cleanNumber + kindCode;
              }

              if (isPublication) {
                const numberPart = nrValue.replace(/[A-Z]\d*$/, '');
                if (numberPart.length >= 5 && numberPart[4] === '0') {
                  const modifiedNumber = numberPart.slice(0, 4) + numberPart.slice(5);
                  nrValue = nrValue.replace(numberPart, modifiedNumber);
                }
              }

              return (
                <a
                  href={`https://worldwide.espacenet.com/publicationDetails/biblio?CC=US&NR=${nrValue}&KC=${kindCode}&FT=D`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors text-blue-600 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  Espacenet (EPO)
                </a>
              );
            })()}

            {applicationData.application_number && (
              <a
                href={`https://globaldossier.uspto.gov/result/application/US/${applicationData.application_number.replace(/[\/,]/g, '')}/1`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors text-blue-600 text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                Global Dossier
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Classification */}
      {(metaData?.class_ || metaData?.subclass || metaData?.cpcClassificationBag) && (
        <Card>
          <CardHeader>
            <CardTitle>Classifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {metaData?.class_ && (
                <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <dt className="text-sm font-medium text-gray-500">Class</dt>
                  <dd><CopyableValue value={metaData.class_} /></dd>
                </div>
              )}

              {metaData?.subclass && (
                <div className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <dt className="text-sm font-medium text-gray-500">Subclass</dt>
                  <dd><CopyableValue value={metaData.subclass} /></dd>
                </div>
              )}

              {metaData?.cpcClassificationBag && Array.isArray(metaData.cpcClassificationBag) && metaData.cpcClassificationBag.length > 0 && (
                <div className="py-2">
                  <dt className="text-sm font-medium text-gray-500 mb-2">CPC Classifications</dt>
                  <dd className="flex flex-wrap gap-2">
                    {metaData.cpcClassificationBag.map((cpc: string, idx: number) => (
                      <CopyableValue key={idx} value={cpc} className="bg-gray-100 text-gray-700 border border-gray-200 rounded-md px-2 py-0.5 text-xs" />
                    ))}
                  </dd>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      </TabsContent>

      <TabsContent value="continuity">
        {/* Application Continuity & Priority */}
        {loadingContinuity ? (
          <Card>
            <CardHeader>
              <CardTitle>Application Continuity & Priority</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Loading continuity information...</span>
              </div>
            </CardContent>
          </Card>
        ) : hasContinuityInfo ? (
          <Card>
            <CardHeader>
              <CardTitle>Application Continuity & Priority</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Foreign Priority */}
                {continuityInfo && continuityInfo.foreign_priority.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 text-gray-800">Foreign Priority Claims</h4>
                    <div className="space-y-2">
                      {continuityInfo.foreign_priority.map((priority: any, index: number) => (
                        <div key={index} className="p-3 rounded border bg-gray-50 border-gray-200">
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-500">Country:</span>
                              <span className="ml-2 text-gray-900">{priority.ipOfficeName}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-500">Application:</span>
                              <span className="ml-2 text-gray-900">{priority.applicationNumberText}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-500">Filing Date:</span>
                              <span className="ml-2 text-gray-900">{formatDate(priority.filingDate)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Parent Applications */}
                {continuityInfo && continuityInfo.parent_applications.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 text-gray-800">Parent Applications</h4>
                    <div className="space-y-2">
                      {continuityInfo.parent_applications.map((parent: any, index: number) => {
                        const parentAppNumber = parent.parentApplicationNumberText || parent.applicationNumberText || parent.applicationNumber;
                        const childAppNumber = parent.childApplicationNumberText;
                        const currentAppNumber = applicationData?.application_number;

                        return (
                          <div key={index} className="p-3 rounded border bg-gray-50 border-gray-200">
                            {/* Application Numbers Flow */}
                            <div className="mb-3 text-sm">
                              <span className="font-medium text-gray-500">Applications:</span>
                              <div className="mt-1 flex items-center gap-2 flex-wrap">
                                <span
                                  className={`px-2 py-1 rounded text-xs ${
                                    parentAppNumber === currentAppNumber
                                      ? 'bg-blue-100 text-blue-800 border border-blue-300 ring-2 ring-blue-400 font-bold'
                                      : 'bg-gray-100 text-gray-700 font-medium'
                                  }`}
                                >
                                  {parentAppNumber} (Parent)
                                </span>
                                <span className="text-gray-400">→</span>
                                <span
                                  className={`px-2 py-1 rounded text-xs ${
                                    childAppNumber === currentAppNumber
                                      ? 'bg-blue-100 text-blue-800 border border-blue-300 ring-2 ring-blue-400 font-bold'
                                      : 'bg-gray-100 text-gray-700 font-medium'
                                  }`}
                                >
                                  {childAppNumber} (Child)
                                </span>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-500">Parent Filing Date:</span>
                                <span className="ml-2 text-gray-900">
                                  {formatDate(parent.filingDate || parent.parentApplicationFilingDate)}
                                </span>
                              </div>
                              {parent.parentPatentNumber && (
                                <div>
                                  <span className="font-medium text-gray-500">Parent Patent Number:</span>
                                  <span className="ml-2 text-gray-900">{parent.parentPatentNumber}</span>
                                </div>
                              )}
                            </div>

                            {/* Status Description with styled badge */}
                            {parent.parentApplicationStatusDescriptionText && (
                              <div className="mt-2 text-sm">
                                <span className="font-medium text-gray-500">Parent Status:</span>
                                <Badge
                                  variant="outline"
                                  className={`ml-2 ${getStatusStyling(parent.parentApplicationStatusCode)}`}
                                  title={parent.parentApplicationStatusCode ? `Status Code: ${parent.parentApplicationStatusCode}` : ''}
                                >
                                  {parent.parentApplicationStatusDescriptionText}
                                </Badge>
                              </div>
                            )}

                            {/* Relationship Type */}
                            {parent.claimParentageTypeCode && (
                              <div className="mt-2 text-sm">
                                <span className="font-medium text-gray-500">Relationship:</span>
                                <span className="ml-2 text-gray-900">
                                  {parent.claimParentageTypeCodeDescription || parent.claimParentageTypeCode}
                                </span>
                              </div>
                            )}

                            {/* First Inventor to File Indicator */}
                            {parent.firstInventorToFileIndicator && (
                              <div className="mt-2 text-sm">
                                <span className="font-medium text-gray-500">First Inventor to File:</span>
                                <span className="ml-2 text-gray-900">
                                  {parent.firstInventorToFileIndicator === true || parent.firstInventorToFileIndicator === 'Y' ? 'Yes' : 'No'}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Child Applications */}
                {continuityInfo && continuityInfo.child_applications.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2 text-gray-800">Child Applications</h4>
                    <div className="space-y-2">
                      {continuityInfo.child_applications.map((child: any, index: number) => {
                        const parentAppNumber = child.parentApplicationNumberText;
                        const childAppNumber = child.childApplicationNumberText || child.applicationNumberText || child.applicationNumber;
                        const currentAppNumber = applicationData?.application_number;

                        return (
                          <div key={index} className="p-3 rounded border bg-gray-50 border-gray-200">
                            {/* Application Numbers Flow */}
                            <div className="mb-3 text-sm">
                              <span className="font-medium text-gray-500">Applications:</span>
                              <div className="mt-1 flex items-center gap-2 flex-wrap">
                                <span
                                  className={`px-2 py-1 rounded text-xs ${
                                    parentAppNumber === currentAppNumber
                                      ? 'bg-blue-100 text-blue-800 border border-blue-300 ring-2 ring-blue-400 font-bold'
                                      : 'bg-gray-100 text-gray-700 font-medium'
                                  }`}
                                >
                                  {parentAppNumber} (Parent)
                                </span>
                                <span className="text-gray-400">→</span>
                                <span
                                  className={`px-2 py-1 rounded text-xs ${
                                    childAppNumber === currentAppNumber
                                      ? 'bg-blue-100 text-blue-800 border border-blue-300 ring-2 ring-blue-400 font-bold'
                                      : 'bg-gray-100 text-gray-700 font-medium'
                                  }`}
                                >
                                  {childAppNumber} (Child)
                                </span>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-500">Child Filing Date:</span>
                                <span className="ml-2 text-gray-900">
                                  {formatDate(child.childApplicationFilingDate || child.filingDate || child.filing_date)}
                                </span>
                              </div>
                              {child.childPatentNumber && (
                                <div>
                                  <span className="font-medium text-gray-500">Child Patent Number:</span>
                                  <span className="ml-2 text-gray-900">{child.childPatentNumber}</span>
                                </div>
                              )}
                            </div>

                            {/* Status Description with styled badge */}
                            {child.childApplicationStatusDescriptionText && (
                              <div className="mt-2 text-sm">
                                <span className="font-medium text-gray-500">Child Status:</span>
                                <Badge
                                  variant="outline"
                                  className={`ml-2 ${getStatusStyling(child.childApplicationStatusCode)}`}
                                  title={child.childApplicationStatusCode ? `Status Code: ${child.childApplicationStatusCode}` : ''}
                                >
                                  {child.childApplicationStatusDescriptionText}
                                </Badge>
                              </div>
                            )}

                            {/* Relationship Type */}
                            {child.claimParentageTypeCode && (
                              <div className="mt-2 text-sm">
                                <span className="font-medium text-gray-500">Relationship:</span>
                                <span className="ml-2 text-gray-900">
                                  {child.claimParentageTypeCodeDescription || child.claimParentageTypeCode}
                                </span>
                              </div>
                            )}

                            {/* First Inventor to File Indicator */}
                            {child.firstInventorToFileIndicator && (
                              <div className="mt-2 text-sm">
                                <span className="font-medium text-gray-500">First Inventor to File:</span>
                                <span className="ml-2 text-gray-900">
                                  {child.firstInventorToFileIndicator === true || child.firstInventorToFileIndicator === 'Y' ? 'Yes' : 'No'}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Application Continuity & Priority</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <p>No continuity or priority information found for this application.</p>
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="prosecution">
        <ProsecutionDocuments
          applicationNumber={applicationData.application_number}
          onDocumentClick={onProsecutionDocClick}
        />
      </TabsContent>

      <TabsContent value="assignments">
        <Assignments
          applicationNumber={applicationData.application_number}
        />
      </TabsContent>
    </Tabs>
  );
}
